import { useMemo, useCallback } from 'react'
import { useLiveChats, useLiveChatCounts } from './useLiveChat.js'
import { useWhatsAppChats, chatDisplayName } from './useWhatsAppChats.js'
import { useFacebookChats } from './useFacebookChats.js'
import { useInstagramChats } from './useInstagramChats.js'
import { useTelegramChats, chatDisplayName as telegramDisplayName } from './useTelegramChats.js'
import { api } from '../api/client.js'
import { useAuthStore } from '../store/authStore.js'
import type { ChatListEntry } from '../pages/agent/chats/ChatConversationList.js'

export type ChatChannel = 'all' | 'chat' | 'facebook' | 'instagram' | 'whatsapp' | 'telegram'
export type ChatStatus = 'all' | 'unassigned' | 'mine' | 'unread'

export interface ChatCounts {
  allChannels: number
  chat: number
  facebook: number
  instagram: number
  whatsapp: number
  telegram: number
  all: number
  unassigned: number
  mine: number
  unread: number
}

interface UseUnifiedChatsReturn {
  entries: ChatListEntry[]
  counts: ChatCounts
  loading: boolean
  markRead: (id: string, channel: string) => void
  refetch: () => Promise<void>
}

export function useUnifiedChats(
  channelFilter: ChatChannel,
  statusFilter: ChatStatus,
  searchQuery: string,
): UseUnifiedChatsReturn {
  const agentId = useAuthStore((s) => s.user?.agentId) ?? ''

  const liveFilters = useMemo(
    () =>
      channelFilter === 'chat'
        ? { status: statusFilter, search: searchQuery.trim() || undefined }
        : undefined,
    [channelFilter, statusFilter, searchQuery],
  )

  const { chats: liveChats, loading: liveLoading, fetchChats: fetchLive } = useLiveChats(liveFilters)
  const { counts: liveCounts, loading: liveCountsLoading, refetch: refetchLiveCounts } = useLiveChatCounts()
  const activeSearch = searchQuery.trim()
  const { chats: whatsappChats, loading: whatsappLoading, fetchChats: fetchWhatsApp, markRead: markWhatsAppRead } =
    useWhatsAppChats(channelFilter === 'whatsapp' || channelFilter === 'all' ? activeSearch : undefined)
  const { chats: facebookChats, loading: facebookLoading, fetchChats: fetchFacebook, markRead: markFacebookRead } =
    useFacebookChats(channelFilter === 'facebook' || channelFilter === 'all' ? activeSearch : undefined)
  const { chats: instagramChats, loading: instagramLoading, fetchChats: fetchInstagram, markRead: markInstagramRead } =
    useInstagramChats(channelFilter === 'instagram' || channelFilter === 'all' ? activeSearch : undefined)
  const { chats: telegramChats, loading: telegramLoading, refetch: fetchTelegram } =
    useTelegramChats(channelFilter === 'telegram' || channelFilter === 'all' ? activeSearch : undefined)

  const normalized = useMemo(() => {
    const all: ChatListEntry[] = []

    for (const c of liveChats) {
      all.push({
        id: c.id,
        customerName: c.visitorName || c.visitorId,
        customerHandle: c.visitorId,
        customerEmail: c.visitorEmail,
        clientId: c.clientId,
        channel: 'chat',
        timestamp: c.lastMessageAt,
        createdAt: c.createdAt,
        preview: c.lastMessageText ?? '',
        isRead: c.isRead,
        unreadCount: c.isRead ? 0 : 1,
        assignedAgentId: c.assignedAgentId,
        aiAssigned: c.aiAssigned,
        aiAgentName: c.aiAgentName,
        status: c.status,
      })
    }

    for (const c of whatsappChats) {
      const name = chatDisplayName(c)
      const isRead = c.unread_count === 0
      all.push({
        id: c.id,
        customerName: name,
        customerHandle: c.wa_id,
        customerEmail: c.client_email,
        customerPhone: c.wa_id,
        clientId: c.client_id,
        channel: 'whatsapp',
        timestamp: c.last_message_at,
        createdAt: c.created_at,
        preview: c.last_message_text ?? '',
        isRead,
        unreadCount: c.unread_count,
        assignedAgentId: c.assigned_agent_id,
        assignedAt: c.assigned_at,
        aiAssigned: c.ai_assigned,
        aiAgentName: c.ai_agent_name,
        status: c.status,
      })
    }

    for (const c of facebookChats) {
      const isRead = c.unread_count === 0
      all.push({
        id: c.id,
        customerName: c.sender_name || c.sender_id,
        customerHandle: c.sender_id,
        customerEmail: c.sender_email,
        channel: 'facebook',
        timestamp: c.last_message_at,
        createdAt: c.created_at,
        preview: c.last_message_text ?? '',
        isRead,
        unreadCount: c.unread_count,
        assignedAgentId: c.assigned_agent_id,
        assignedAt: c.assigned_at,
        aiAssigned: c.ai_assigned,
        aiAgentName: c.ai_agent_name,
        status: c.status,
      })
    }

    for (const c of instagramChats) {
      const isRead = c.unread_count === 0
      all.push({
        id: c.id,
        customerName: c.sender_name || c.sender_id,
        customerHandle: c.sender_id,
        channel: 'instagram',
        timestamp: c.last_message_at,
        createdAt: c.created_at,
        preview: c.last_message_text ?? '',
        isRead,
        unreadCount: c.unread_count,
        assignedAgentId: c.assigned_agent_id,
        assignedAt: c.assigned_at,
        aiAssigned: c.ai_assigned,
        aiAgentName: c.ai_agent_name,
        status: c.status,
      })
    }

    for (const c of telegramChats) {
      const name = telegramDisplayName(c)
      const isRead = c.unread_count === 0
      all.push({
        id: c.id,
        customerName: name,
        customerHandle: c.telegram_user_id ?? c.telegram_chat_id,
        customerEmail: c.client_email,
        clientId: c.client_id,
        channel: 'telegram',
        timestamp: c.last_message_at,
        createdAt: c.created_at,
        preview: c.last_message_text ?? '',
        isRead,
        unreadCount: c.unread_count,
        assignedAgentId: c.assigned_agent_id,
        assignedAt: c.assigned_at,
        aiAssigned: c.ai_assigned,
        aiAgentName: c.ai_agent_name,
        status: c.status,
        chatType: c.chat_type,
      })
    }

    all.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    return all
  }, [liveChats, whatsappChats, facebookChats, instagramChats, telegramChats])

  const search = searchQuery.trim().toLowerCase()
  const searched = useMemo(() => {
    if (!search) return normalized
    return normalized.filter(
      (e) =>
        e.customerName.toLowerCase().includes(search) ||
        e.preview.toLowerCase().includes(search) ||
        e.id.toLowerCase().includes(search),
    )
  }, [normalized, search])

  const filtered = useMemo(() => {
    return searched.filter((e) => {
      if (channelFilter !== 'all' && e.channel !== channelFilter) return false

      if (statusFilter === 'all') return true
      if (statusFilter === 'unassigned') return e.assignedAgentId === null
      if (statusFilter === 'mine') return e.assignedAgentId === agentId
      if (statusFilter === 'unread') return !e.isRead
      return true
    })
  }, [searched, channelFilter, statusFilter, agentId])

  const counts = useMemo((): ChatCounts => {
    const total = (v: boolean[]) => v.filter(Boolean).length
    const byChannel = (ch: string) => searched.filter((e) => e.channel === ch)
    const allChannels = searched.length
    const chat = liveCounts.all
    const facebook = byChannel('facebook').length
    const instagram = byChannel('instagram').length
    const whatsapp = byChannel('whatsapp').length
    const telegram = byChannel('telegram').length

    if (channelFilter === 'chat') {
      return {
        allChannels,
        chat,
        facebook,
        instagram,
        whatsapp,
        telegram,
        all: liveCounts.all,
        unassigned: liveCounts.unassigned,
        mine: liveCounts.mine,
        unread: liveCounts.unread,
      }
    }

    const scoped = channelFilter === 'all' ? searched : searched.filter((e) => e.channel === channelFilter)
    return {
      allChannels,
      chat,
      facebook,
      instagram,
      whatsapp,
      telegram,
      all: scoped.length,
      unassigned: total(scoped.map((e) => e.assignedAgentId === null)),
      mine: total(scoped.map((e) => e.assignedAgentId === agentId)),
      unread: total(scoped.map((e) => !e.isRead)),
    }
  }, [searched, channelFilter, agentId, liveCounts])

  const markRead = useCallback(
    (id: string, channel: string) => {
      if (channel === 'chat') {
        void api.post(`/agent/live-chats/${id}/read`, {}).then(() => {
          void fetchLive()
          void refetchLiveCounts()
        })
        return
      }
      if (channel === 'whatsapp') {
        void markWhatsAppRead(id)
        return
      }
      if (channel === 'facebook') {
        void markFacebookRead(id)
        return
      }
      if (channel === 'instagram') {
        void markInstagramRead(id)
        return
      }
      if (channel === 'telegram') {
        void api.post(`/agent/telegram-chats/${id}/read`, {}).then(() => void fetchTelegram())
      }
    },
    [fetchLive, markWhatsAppRead, markFacebookRead, markInstagramRead, fetchTelegram, refetchLiveCounts],
  )

  const refetch = useCallback(async (): Promise<void> => {
    await Promise.all([fetchLive(), fetchWhatsApp(), fetchFacebook(), fetchInstagram(), fetchTelegram(), refetchLiveCounts()])
  }, [fetchLive, fetchWhatsApp, fetchFacebook, fetchInstagram, fetchTelegram, refetchLiveCounts])

  const loading = liveLoading || liveCountsLoading || whatsappLoading || facebookLoading || instagramLoading || telegramLoading

  return { entries: filtered, counts, loading, markRead, refetch }
}
